/**
 * Disaster Recovery Context
 * This context manages the disaster recovery mode state across the application to display global EAP phone numbers and error messages during outages
 *
 * This context can be used in both React components and non-React code.
 * It provides a way to set and get the disaster recovery mode state globally with global ref
 * The Context + Ref Pattern is used to allow both React components and non-React code to access the same context value.
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import queryString from 'query-string';
import { AuthClient, CORRELATION_ID_KEY } from '@workivate/tho-web-shared';

interface DisasterRecoveryContextType {
  isInDisasterRecoveryMode: boolean;
  setIsInDisasterRecoveryMode: (value: boolean) => void;
  showTroubleshootingPanel: boolean;
  setShowTroubleshootingPanel: (value: boolean) => void;
}

const DisasterRecoveryContext = createContext<DisasterRecoveryContextType>({
  isInDisasterRecoveryMode: false,
  setIsInDisasterRecoveryMode: () => {},
  showTroubleshootingPanel: false,
  setShowTroubleshootingPanel: () => {},
});

/** Global ref to store the context value */
let disasterRecoveryContextRef: DisasterRecoveryContextType | null = null;

/** sessionStorage key for the auth client ID saved before logout clears it */
export const AUTH_ERROR_CLIENT_ID_KEY = 'authErrorClientId';

/** sessionStorage key for the correlation ID saved before logout clears it */
export const AUTH_ERROR_CORRELATION_ID_KEY = 'authErrorCorrelationId';

/**
 * Save auth error IDs to sessionStorage before logout / redirect clears them.
 *
 * AuthClient.deleteAuthSession() removes _authclientId from localStorage; logout()
 * removes other keys. Call this before any operation that clears those values so
 * TroubleshootingDetails can display them on the login page after the redirect.
 * The saved values are cleared by setUnexpectedAuthError(false) when the user
 * next clicks the login button.
 */
export const saveAuthErrorIds = (): void => {
  const clientId = AuthClient.clientId;
  const correlationId = localStorage.getItem(CORRELATION_ID_KEY);

  if (clientId) {
    sessionStorage.setItem(AUTH_ERROR_CLIENT_ID_KEY, clientId);
  } else {
    sessionStorage.removeItem(AUTH_ERROR_CLIENT_ID_KEY);
  }

  if (correlationId) {
    sessionStorage.setItem(AUTH_ERROR_CORRELATION_ID_KEY, correlationId);
  } else {
    sessionStorage.removeItem(AUTH_ERROR_CORRELATION_ID_KEY);
  }
};

export const DisasterRecoveryProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isInDisasterRecoveryMode, setIsInDisasterRecoveryMode] =
    useState(false);
  const [showTroubleshootingPanel, setShowTroubleshootingPanel] =
    useState(false);

  const contextValue = useMemo(
    () => ({
      isInDisasterRecoveryMode,
      setIsInDisasterRecoveryMode,
      showTroubleshootingPanel,
      setShowTroubleshootingPanel,
    }),
    [isInDisasterRecoveryMode, showTroubleshootingPanel],
  );

  /*
   * Update the global ref whenever the context value changes.
   * No cleanup here — we never want the ref to be momentarily null between
   * re-renders (which would cause getDisasterRecoveryContext() to throw if
   * called in the brief window between the cleanup and the next assignment).
   */
  useEffect(() => {
    disasterRecoveryContextRef = contextValue;
  }, [contextValue]);

  // Null the ref only when the provider actually unmounts.
  useEffect(() => {
    return () => {
      disasterRecoveryContextRef = null;
    };
  }, []);

  // Check URL params and pending disaster recovery mode on mount
  useEffect(() => {
    const query = queryString.parse(location.search);
    if (query.error === 'server_error') {
      /*
       * saveAuthErrorIds() is called here rather than via setUnexpectedAuthError() because
       * this code runs inside the provider's own useEffect — the global ref is not yet set
       * at this point, so setUnexpectedAuthError() would fall into the sessionStorage fallback
       * path and the pendingUnexpectedAuthError check earlier in this same useEffect would
       * already have passed. Calling the setters directly avoids that ordering issue.
       */
      saveAuthErrorIds();
      setIsInDisasterRecoveryMode(true);
      setShowTroubleshootingPanel(true);
    }

    /*
     * Check if there's a pending disaster recovery mode from before the provider was mounted.
     *
     * TIMING ISSUE SOLUTION: pendingDisasterRecoveryMode handles a race condition in the USL authentication flow
     *
     * Problem: In run-with-usl.tsx, AuthEvents listeners are set up to call setDisasterRecoveryMode(true)
     * when a 500 error occurs during token exchange. However, these listeners are active BEFORE the React
     * app renders and the DisasterRecoveryProvider mounts. This creates a timing issue where:
     * 1. USL authentication starts and sets up AuthEvents listeners
     * 2. A 500 error occurs during token exchange, triggering setDisasterRecoveryMode(true)
     * 3. setDisasterRecoveryMode() tries to access the context, but DisasterRecoveryProvider isn't mounted yet
     * 4. This results in "DisasterRecoveryProvider not mounted or context not available" error
     *
     * Solution: When setDisasterRecoveryMode() is called before the provider is mounted:
     * 1. The fallback mechanism catches the "not mounted" error
     * 2. Instead of crashing, it stores the disaster recovery state in sessionStorage as 'pendingDisasterRecoveryMode'
     * 3. When the DisasterRecoveryProvider finally mounts, this useEffect checks for any pending state
     * 4. If found, it applies the pending disaster recovery mode and cleans up the temporary storage
     *
     * This ensures that disaster recovery mode is properly activated even when the timing of authentication
     * errors occurs before React components are fully initialized, maintaining the user experience without crashes.
     */
    const pendingMode = sessionStorage.getItem('pendingDisasterRecoveryMode');
    if (pendingMode !== null) {
      setIsInDisasterRecoveryMode(pendingMode === 'true');
      // Clear the pending mode after applying it
      sessionStorage.removeItem('pendingDisasterRecoveryMode');
    }

    const pendingAuthError = sessionStorage.getItem(
      'pendingUnexpectedAuthError',
    );
    if (pendingAuthError !== null) {
      setShowTroubleshootingPanel(pendingAuthError === 'true');
      sessionStorage.removeItem('pendingUnexpectedAuthError');
    }
  }, []);

  return (
    <DisasterRecoveryContext.Provider value={contextValue}>
      {children}
    </DisasterRecoveryContext.Provider>
  );
};

/** Custom hook for React components */
export const useDisasterRecovery = () => {
  const context = useContext(DisasterRecoveryContext);
  if (!context) {
    throw new Error(
      'useDisasterRecovery must be used within DisasterRecoveryProvider',
    );
  }
  return context;
};

/** Function for non-React code */
export const getDisasterRecoveryContext = () => {
  if (!disasterRecoveryContextRef) {
    throw new Error(
      'DisasterRecoveryProvider not mounted or context not available',
    );
  }
  return disasterRecoveryContextRef;
};

/** Convenience function for non-React code */
export const setDisasterRecoveryMode = (value: boolean) => {
  try {
    const context = getDisasterRecoveryContext();
    context.setIsInDisasterRecoveryMode(value);
  } catch (error) {
    /*
     * FALLBACK MECHANISM: Handle the timing issue when setDisasterRecoveryMode is called before provider mounting.
     * This catch block implements the fallback mechanism described in the detailed comment above.
     * When AuthEvents listeners trigger setDisasterRecoveryMode during USL authentication (before React renders),
     * we gracefully store the disaster recovery state in sessionStorage instead of crashing the application.
     */
    if (
      error instanceof Error &&
      error.message.includes('DisasterRecoveryProvider not mounted')
    ) {
      console.warn(
        'DisasterRecoveryProvider not yet mounted, storing disaster recovery mode for later application:',
        value,
      );
      // Store the disaster recovery mode to be applied when the provider mounts
      sessionStorage.setItem('pendingDisasterRecoveryMode', value.toString());
      return;
    }
    throw error;
  }
};

export const getDisasterRecoveryMode = () => {
  const context = getDisasterRecoveryContext();
  return context.isInDisasterRecoveryMode;
};

/**
 * Convenience function for non-React code to signal an unexpected auth error.
 *
 * Always writes to sessionStorage so the value survives component remounts
 * (e.g. when logout() triggers a Redux state change that re-renders the login page).
 * The DisasterRecoveryProvider reads pendingUnexpectedAuthError from sessionStorage on mount.
 *
 * When called with true, saveAuthErrorIds() is called first to persist the current
 * AuthClient.clientId and CORRELATION_ID_KEY to sessionStorage before any logout/redirect
 * clears them from localStorage. This ensures TroubleshootingDetails always has IDs to
 * display, regardless of which code path triggers the unexpected auth error.
 */
export const setUnexpectedAuthError = (value: boolean) => {
  // Always persist to sessionStorage so the value survives any remount
  if (value) {
    /*
     * Save IDs before writing the flag — logout() and AuthClient.deleteAuthSession()
     * clear localStorage (including _authclientId and CORRELATION_ID_KEY), so we must
     * capture them now while they are still available.
     */
    saveAuthErrorIds();
    sessionStorage.setItem('pendingUnexpectedAuthError', value.toString());
  } else {
    sessionStorage.removeItem('pendingUnexpectedAuthError');
    // Clear any saved auth error IDs (clientId / correlationId) when the error is dismissed
    sessionStorage.removeItem(AUTH_ERROR_CLIENT_ID_KEY);
    sessionStorage.removeItem(AUTH_ERROR_CORRELATION_ID_KEY);
  }
  try {
    const context = getDisasterRecoveryContext();
    context.setShowTroubleshootingPanel(value);
  } catch (error) {
    if (
      error instanceof Error &&
      error.message.includes('DisasterRecoveryProvider not mounted')
    ) {
      // sessionStorage already set above - will be picked up on mount
      return;
    }
    throw error;
  }
};
